/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useCallback, useRef, useState } from "react"

import { TTRDeviceFormData } from "../../../../../components/devices/ttr/types/providerTypes"
import { SAVE_DRAFT_SUCCESS } from "../../../../../constants/messages"
import { SUPPORT_REFER } from "../../../../../errors/errorMessages"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import {
    snackbar,
    getTsIoMapper,
} from "../../../../../utils/general/generalUtils"
import { getTypeTestPostBodyFromFormData } from "../../../../../utils/registerDevice/registerDeviceUtils"
import { postClientProtected } from "../../../clients/clients"
import {
    TSaveDeviceDraftPostBody,
    TSaveDeviceDraftPostBodyPartial,
    TTypeTestVersionPostBody,
    CreateDraftDeviceAPIResponse,
} from "../../../types/devices/ttr/publishDeviceTypes"
import { SAVE_DEVICE_DRAFT } from "../../../urls/private"

const saveLTTRDeviceDraftMapper = getTsIoMapper(
    CreateDraftDeviceAPIResponse,
    "saveLTTRDeviceDraftMapper"
)

export const createDeviceDraftPostBody = (
    formData: TTRDeviceFormData,
    deviceId: string,
    deviceRef: string,
    typeTestVersionPostBody?: TTypeTestVersionPostBody,
    attachmentUUID?: string,
    supersededDeviceID?: number,
    complianceComments?: string,
    complianceStatusNotes?: string
): TSaveDeviceDraftPostBody => {
    const registeredCapacity =
        formData.coreDetails.registeredCapacity === ""
            ? undefined
            : formData.coreDetails.registeredCapacity

    const saveDraftPostBodyPartial: TSaveDeviceDraftPostBodyPartial = {
        ManufacturerID: formData.coreDetails.manufacturer ?? undefined,
        DeviceID: deviceId,
        DeviceRef: deviceRef,
        ModelNo: formData.coreDetails.model,
        DeviceCategoryID: formData.coreDetails.deviceCategory ?? undefined,
        DeviceTypeID: formData.coreDetails.deviceType ?? undefined,
        RegisteredCapacity: registeredCapacity ?? undefined,
        SoftwareVersion: formData.coreDetails.softwareVersion,
        HardwareVersion: formData.coreDetails.hardwareVersion,
        DeviceComments: formData.coreDetails.additionalComments,
        PhaseID: formData.coreDetails.numberOfPhases ?? undefined,
        AttachmentUUID: attachmentUUID,
        SupersededDeviceID: supersededDeviceID,
        ComplianceComments: complianceComments,
        ComplianceStatusNotes: complianceStatusNotes,
    }

    const typeTestPostBody = getTypeTestPostBodyFromFormData(formData)

    return typeTestVersionPostBody
        ? {
              ...saveDraftPostBodyPartial,
              ...typeTestVersionPostBody,
              ...typeTestPostBody,
          }
        : {
              ...saveDraftPostBodyPartial,
              ...typeTestPostBody,
          }
}

interface IUseSaveDeviceDraftOutput {
    saveDeviceDraft: (formData: TTRDeviceFormData) => Promise<void>
    isLoading: boolean
    isLoaded: boolean
    error?: AppError
}

const useSaveDeviceDraft = (
    deviceRef?: string,
    deviceId?: string,
    attachmentUUID?: string,
    supersededDeviceID?: number,
    complianceComments?: string,
    complianceStatusNotes?: string,
    typeTestVersionPostBody?: TTypeTestVersionPostBody
): IUseSaveDeviceDraftOutput => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<AppError | undefined>(undefined)
    const [isLoaded, setIsLoaded] = useState<boolean>(false)
    const apiDeviceId = useRef<string | undefined>(deviceId)

    const saveDeviceDraft = useCallback(
        async (formData: TTRDeviceFormData): Promise<void> => {
            if (!deviceId || !deviceRef) {
                return
            }

            const postBody = createDeviceDraftPostBody(
                formData,
                deviceId || apiDeviceId.current || "",
                deviceRef,
                typeTestVersionPostBody,
                attachmentUUID,
                supersededDeviceID,
                complianceComments,
                complianceStatusNotes
            )

            try {
                setIsLoading(true)
                const response = await postClientProtected(
                    SAVE_DEVICE_DRAFT,
                    postBody
                )
                const mappedRes = saveLTTRDeviceDraftMapper.map(response)
                apiDeviceId.current = mappedRes.SystemReference
                snackbar(SAVE_DRAFT_SUCCESS, "success")
                setIsLoaded(true)
            } catch {
                const appError = invalidApiDataError("Couldn't save draft")
                setError(appError)
                snackbar(
                    `Couldn't save draft, please try again. ${SUPPORT_REFER}`,
                    "warning"
                )
            } finally {
                setIsLoading(false)
            }
        },
        [
            attachmentUUID,
            complianceComments,
            complianceStatusNotes,
            deviceId,
            deviceRef,
            supersededDeviceID,
            typeTestVersionPostBody,
        ]
    )

    return {
        saveDeviceDraft,
        isLoaded,
        isLoading,
        error,
    }
}

export default useSaveDeviceDraft
